import agent from 'agent'
import {
  type TeamExternalIntegration,
  type ResponseBodyEvaluation,
  type EndpointResponse
} from 'types/api/status-checks/status-check'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import { ENDPOINT_URLS } from 'utils/third-party'

export const pagerDutyStatusCheckIntegration: TeamExternalIntegration = {
  name: 'PagerDuty',
  type: IntegrationType.Pagerduty,
  authUrl: `${ENDPOINT_URLS.pagerduty}abilities`,
  isPrivateNetworkIntegration: false,
  headers: [{ key: 'Authorization', value: '', label: 'API Key' }],
  url: ENDPOINT_URLS.pagerduty,
  sortIndex: 1
}

// Example URLs
// https://api.pagerduty.com/incidents?statuses[]=triggered&service_ids[]=P1F04YY
// https://gremlininc.pagerduty.com/service-directory/P1F04YY
export const pagerDutyRegex = new RegExp(
  '^https:\\/\\/(.+).pagerduty.com\\/(incidents|service-directory)(.|\\/)(.*)$'
)

export const pagerDutyResponseBodyEvaluationConfiguration = {
  op: 'AND',
  predicates: [
    {
      jpQuery: '.incidents.length()',
      type: 'Number',
      comparator: 'EQU',
      rValue: '0'
    }
  ]
} as ResponseBodyEvaluation

export async function convertToPagerDutyUrl(
  integration: TeamExternalIntegration,
  teamId: string
): Promise<string> {
  const { url } = integration
  if (!pagerDutyRegex.test(url)) {
    return null
  }

  const defaultPagerDutyApiURL = `${ENDPOINT_URLS.pagerduty}incidents?statuses[]=triggered`

  const regExpExecArray = pagerDutyRegex.exec(url)
  const incidentOrServiceWord = regExpExecArray?.[2]
  const mightIncidentOrServiceIdBePresent =
    regExpExecArray?.[3] === '/' || regExpExecArray?.[3] === '?'
  if (!mightIncidentOrServiceIdBePresent) {
    return defaultPagerDutyApiURL
  }

  // try to get service id directly from resolved URL
  const serviceIdFromQueryParams =
    regExpExecArray?.[3] === '?' &&
    regExpExecArray?.[4]?.includes('service_ids[]=')
      ? new URLSearchParams(regExpExecArray?.[4]).getAll('service_ids[]')?.[0]
      : null

  const incidentOrServiceId = serviceIdFromQueryParams || regExpExecArray?.[4]
  const hasServiceDirectoryId =
    incidentOrServiceWord === 'service-directory' && incidentOrServiceId

  if (hasServiceDirectoryId || serviceIdFromQueryParams) {
    return `${ENDPOINT_URLS.pagerduty}incidents?statuses[]=triggered&service_ids[]=${incidentOrServiceId}`
  } else if (incidentOrServiceId) {
    // we have an incidentId, need to retrieve its service
    const request = {
      endpointConfiguration: {
        url: `${ENDPOINT_URLS.pagerduty}incidents/${incidentOrServiceId}`,
        headers: integration.headers.reduce((result, item) => {
          result[item.key] = item.value
          return result
        }, {})
      },
      ...(integration.isConfigured && {
        teamExternalIntegration: {
          type: integration.type?.toUpperCase(),
          name: integration.name
        }
      })
    }

    // try to get service from incident
    try {
      const { data } = await agent.post(
        `status-checks/run-test-endpoint?teamId=${teamId}`,
        request
      )
      const serviceId = JSON.parse(data?.testEndpointResponse?.responseBody)
        ?.incident?.service?.id
      if (serviceId) {
        return `${ENDPOINT_URLS.pagerduty}incidents?statuses[]=triggered&service_ids[]=${serviceId}`
      }
    } catch (err) {
      // defaultPagerDutyApiURL will be returned in this case
    }
  }

  return defaultPagerDutyApiURL
}

export function getPagerdutyServiceName(
  testEndpointResponse: EndpointResponse,
  integration: TeamExternalIntegration
): string {
  if (integration.url.includes('services'))
    return JSON.parse(testEndpointResponse?.responseBody)?.service?.name
}
